import "./education_section.scss";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Body } from "@sprout/typography";
import { IconButton } from "@sprout/icon_button";
import TrashIcon from "@sprout/icons/trash";
import { AsyncSingleSelect, SingleSelect, type Option } from "@sprout/select";
import { useLoadAsyncOptions } from "@sprout/select/hooks";
import { TextInput } from "@sprout/text_input";
import {
  type EducationConfiguration,
  type EducationConfigurationOption,
} from "../../types/jben/job_post";
import { type EducationErrors } from "../../utils/jben/validate_application";
import { focusLastSection } from "../../utils/jben/focus_last_section";
import { useMonthOptions } from "../../hooks/use_month_options";
import { useEffectAfterMount } from "../../hooks/use_effect_after_mount";

const DEFAULT_EDUCATION_PATH = "/education";

export interface EducationAnswer {
  key: number;
  school?: Option;
  degree?: Option;
  discipline?: Option;
  start_month?: Option;
  start_year?: number;
  end_month?: Option;
  end_year?: number;
}

interface EducationProps {
  answer: EducationAnswer;
  setAnswer: (answer: EducationAnswer) => void;
  showDelete: boolean;
  deleteAnswer: () => void;
  error?: EducationErrors;
  educationConfiguration: EducationConfiguration;
  educationPath: string;
}

interface Props {
  answers: EducationAnswer[];
  errors: Record<number, EducationErrors>;
  educationConfiguration: EducationConfiguration;
  onChange: (educationAnswers: EducationAnswer[]) => void;
  educationPath?: string;
}

function isRequired(option: EducationConfigurationOption): boolean {
  return option === "required";
}

function shouldDisplay(option: EducationConfigurationOption): boolean {
  return option === "optional" || option === "required";
}

const Education = ({
  answer,
  setAnswer,
  showDelete,
  deleteAnswer,
  error,
  educationConfiguration,
  educationPath,
}: EducationProps) => {
  const { t } = useTranslation("job_post");
  const monthOptions = useMonthOptions();

  const [school, setSchool] = useState<Option | null>(answer.school || null);
  const [degree, setDegree] = useState<Option | null>(answer.degree || null);
  const [discipline, setDiscipline] = useState<Option | null>(answer.discipline || null);
  const [startMonth, setStartMonth] = useState<Option | null>(answer.start_month || null);
  const [startYear, setStartYear] = useState<number | undefined>(answer.start_year);
  const [endMonth, setEndMonth] = useState<Option | null>(answer.end_month || null);
  const [endYear, setEndYear] = useState<number | undefined>(answer.end_year);

  const loadSchoolOptions = useLoadAsyncOptions(`${educationPath}/schools`);
  const loadDegreeOptions = useLoadAsyncOptions(`${educationPath}/degrees`);
  const loadDisciplineOptions = useLoadAsyncOptions(`${educationPath}/disciplines`);

  useEffectAfterMount(() => {
    setAnswer({
      key: answer.key,
      school: school || undefined,
      degree: degree || undefined,
      discipline: discipline || undefined,
      start_month: startMonth || undefined,
      start_year: startYear,
      end_month: endMonth || undefined,
      end_year: endYear,
    });
  }, [school, degree, discipline, startMonth, startYear, endMonth, endYear]);

  const displayStartMonth = shouldDisplay(educationConfiguration.start_month);
  const displayStartYear = shouldDisplay(educationConfiguration.start_year);
  const displayEndMonth = shouldDisplay(educationConfiguration.end_month);
  const displayEndYear = shouldDisplay(educationConfiguration.end_year);

  const parseYear = (value: string): number | undefined => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  return (
    <div className="education--form">
      <hr />
      <div className="education--header">
        <Body medium>{t("education.title")}</Body>
        {showDelete && (
          <IconButton label={t("education.remove")} icon={TrashIcon} onClick={deleteAnswer} />
        )}
      </div>
      {shouldDisplay(educationConfiguration.school_name) && (
        <AsyncSingleSelect
          label={t("education.labels.school")}
          value={school}
          onChange={setSchool}
          loadOptions={loadSchoolOptions}
          clearable
          required={isRequired(educationConfiguration.school_name)}
          error={error?.school}
        />
      )}
      {shouldDisplay(educationConfiguration.degree) && (
        <AsyncSingleSelect
          label={t("education.labels.degree")}
          value={degree}
          onChange={setDegree}
          loadOptions={loadDegreeOptions}
          clearable
          required={isRequired(educationConfiguration.degree)}
          error={error?.degree}
        />
      )}
      {shouldDisplay(educationConfiguration.discipline) && (
        <AsyncSingleSelect
          label={t("education.labels.discipline")}
          value={discipline}
          onChange={setDiscipline}
          loadOptions={loadDisciplineOptions}
          clearable
          required={isRequired(educationConfiguration.discipline)}
          error={error?.discipline}
        />
      )}
      {(displayStartMonth || displayStartYear || displayEndMonth || displayEndYear) && (
        <div className="education--half-width-container">
          {(displayStartMonth || displayStartYear) && (
            <div className="education--date-container">
              {displayStartMonth && (
                <SingleSelect
                  label={t("education.labels.start_month")}
                  options={monthOptions}
                  value={startMonth}
                  onChange={setStartMonth}
                  clearable
                  required={isRequired(educationConfiguration.start_month)}
                  error={error?.start_month}
                />
              )}
              {displayStartYear && (
                <TextInput
                  id={`start-year--${answer.key}`}
                  label={t("education.labels.start_year")}
                  type="number"
                  value={startYear?.toString() || ""}
                  onChange={(event) => setStartYear(parseYear(event.currentTarget.value))}
                  required={isRequired(educationConfiguration.start_year)}
                  error={error?.start_year}
                />
              )}
            </div>
          )}
          {(displayEndMonth || displayEndYear) && (
            <div className="education--date-container">
              {displayEndMonth && (
                <SingleSelect
                  label={t("education.labels.end_month")}
                  options={monthOptions}
                  value={endMonth}
                  onChange={setEndMonth}
                  clearable
                  required={isRequired(educationConfiguration.end_month)}
                  error={error?.end_month}
                />
              )}
              {displayEndYear && (
                <TextInput
                  id={`end-year--${answer.key}`}
                  label={t("education.labels.end_year")}
                  type="number"
                  value={endYear?.toString() || ""}
                  onChange={(event) => setEndYear(parseYear(event.currentTarget.value))}
                  required={isRequired(educationConfiguration.end_year)}
                  error={error?.end_year}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const EducationSection = ({
  educationConfiguration,
  onChange,
  answers,
  errors,
  educationPath = DEFAULT_EDUCATION_PATH,
}: Props) => {
  const { t } = useTranslation("job_post");
  const [shouldFocusSection, setShouldFocusSection] = useState<boolean>(false);

  useEffect(() => {
    if (shouldFocusSection) {
      focusLastSection(".education--form");
      setShouldFocusSection(false);
    }
  }, [shouldFocusSection]);

  return (
    <div className="education--container">
      {answers.map((answer, index) => (
        <Education
          key={answer.key.toString()}
          answer={answer}
          setAnswer={(updatedAnswer) => {
            const updatedAnswers = [...answers];
            updatedAnswers[index] = updatedAnswer;
            onChange(updatedAnswers);
          }}
          showDelete={answers.length > 1}
          deleteAnswer={() => onChange(answers.filter((a) => a.key !== answer.key))}
          error={errors[index]}
          educationConfiguration={educationConfiguration}
          educationPath={educationPath}
        />
      ))}
      <button
        className="add-another-button"
        type="button"
        onClick={() => {
          const lastKey = answers[answers.length - 1].key;
          onChange([...answers, { key: lastKey + 1 }]);
          setShouldFocusSection(true);
        }}
      >
        {t("education.add_another")}
      </button>
    </div>
  );
};
